// ============================================================================
// CHANGO V5.0 — <SmsCodeInput> 短信验证码组件 (§9.6.2)
// ============================================================================
// 复用组件：手机号输入 + "获取验证码"按钮 + 验证码输入 + 60s 倒计时
// 用于试用激活、重新提交资料、公司名变更等场景
//
// Props:
//   - phone: 当前手机号值
//   - phoneError: 手机号错误信息（来自 hook-form）
//   - codeValue: 验证码值
//   - codeError: 验证码错误信息
//   - onPhoneChange: 手机号 onChange 回调
//   - onCodeChange: 验证码 onChange 回调
//   - onSendCode: 点击"获取验证码"的回调，返回 Promise
//   - scope: 短信 scope（'trial_activation' / 'resubmit_license' / 'change_company_name'）
//   - phoneRegister: react-hook-form register 对象（可选）
//   - codeRegister: react-hook-form register 对象（可选）
// ============================================================================

import React, { useState, useEffect } from 'react';
import { HttpError } from '../api/client.js';

export default function SmsCodeInput({
  phone = '',
  phoneError = null,
  codeValue = '',
  codeError = null,
  onPhoneChange,
  onCodeChange,
  onSendCode,
  scope = 'trial_activation',
  phoneRegister = null,
  codeRegister = null,
  disabled = false
}) {
  const [sendingCode, setSendingCode] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [sendError, setSendError] = useState(null);

  useEffect(() => {
    if (countdown <= 0) return;
    const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
    return () => clearTimeout(timer);
  }, [countdown]);

  const handleSendCode = async () => {
    setSendError(null);
    setSendingCode(true);
    try {
      await onSendCode({ phone, scope });
      setCountdown(60);
    } catch (err) {
      const msg = err instanceof HttpError ? err.message : '发送失败，请重试';
      setSendError(msg);
    } finally {
      setSendingCode(false);
    }
  };

  const canSend = phone && phone.length === 11 && countdown === 0 && !disabled;

  return (
    <>
      {/* 手机号行 */}
      <div>
        <label className="label" htmlFor="phone">
          <span className="text-red-600">*</span> 手机号
        </label>
        {phoneRegister ? (
          <input
            id="phone"
            type="tel"
            placeholder="11 位中国大陆手机号"
            className={`field ${phoneError ? 'field-error' : ''}`}
            {...phoneRegister}
          />
        ) : (
          <input
            id="phone"
            type="tel"
            placeholder="11 位中国大陆手机号"
            value={phone}
            onChange={(e) => onPhoneChange?.(e.target.value)}
            disabled={disabled}
            className={`field ${phoneError ? 'field-error' : ''}`}
          />
        )}
        {phoneError ? <p className="err">{phoneError}</p> : null}
      </div>

      {/* 验证码行 */}
      <div>
        <label className="label" htmlFor="sms_code">
          <span className="text-red-600">*</span> 短信验证码
        </label>
        <div className="flex gap-2">
          {codeRegister ? (
            <input
              id="sms_code"
              type="text"
              placeholder="6 位数字"
              maxLength="6"
              className={`flex-1 field ${codeError ? 'field-error' : ''}`}
              {...codeRegister}
            />
          ) : (
            <input
              id="sms_code"
              type="text"
              placeholder="6 位数字"
              maxLength="6"
              value={codeValue}
              onChange={(e) => onCodeChange?.(e.target.value)}
              disabled={disabled}
              className={`flex-1 field ${codeError ? 'field-error' : ''}`}
            />
          )}
          <button
            type="button"
            onClick={handleSendCode}
            disabled={!canSend || sendingCode}
            className="btn btn-outline whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {countdown > 0 ? `重试 (${countdown}s)` : sendingCode ? '发送中…' : '获取验证码'}
          </button>
        </div>
        {codeError ? <p className="err">{codeError}</p> : null}
        {sendError ? <p className="err">{sendError}</p> : null}
      </div>
    </>
  );
}
